import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SaleGuestNamesService } from '@app/sales/sales/sale-guest-names.service';
import { environment } from '@env/environment';
import * as moment from 'moment';

interface FinalGuestNamesNightViewModel {
  date: string;
  guestNames: Array<string | null>;
  requestGuestNames: Array<string | null>;
  manualOverride: boolean;
}

interface FinalGuestNamesItemViewModel {
  sale_final_item_id: number;
  hotel_booking_room_id: number;
  hotel_booking_room_confirmation_number: string | null;
  confirmationNumberDisabled: boolean;
  room: any;
  booking: any;
  sale_final_item: any;
  peoplePerRoom: number;
  nights: Array<FinalGuestNamesNightViewModel>;
}

@Component({
  selector: 'app-sale-finals-guest-names-form',
  templateUrl: './sale-finals-guest-names-form.component.html',
  styleUrls: ['./sale-finals-guest-names-form.component.scss'],
})
export class SaleFinalsGuestNamesFormComponent implements OnInit {
  @Input() saleId: number = null;
  @Input() finalId: number = null;

  env = environment;
  form: FormGroup;
  items: Array<FinalGuestNamesItemViewModel> = [];
  loading = false;

  constructor(public modal: NgbActiveModal, private formBuilder: FormBuilder, private saleGuestNamesService: SaleGuestNamesService) {}

  ngOnInit(): void {
    this.loadData();
  }

  get itemGroups(): FormArray {
    return this.form.get('items') as FormArray;
  }

  getNights(itemGroup: AbstractControl): FormArray {
    return (itemGroup as FormGroup).get('nights') as FormArray;
  }

  getGuestNames(nightGroup: AbstractControl): FormArray {
    return (nightGroup as FormGroup).get('guest_names') as FormArray;
  }

  save(): void {
    this.saleGuestNamesService.store(this.saleId, this.finalId, this.formatPost()).subscribe((res: any) => {
      this.modal.close(res);
    });
  }

  private loadData(): void {
    this.loading = true;

    this.saleGuestNamesService.index(this.saleId, this.finalId).subscribe((response: Array<any>) => {
      this.items = this.decorateItems(Array.isArray(response) ? response : []);
      this.buildForm();
      this.loading = false;
    });
  }

  private decorateItems(rawItems: Array<any>): Array<FinalGuestNamesItemViewModel> {
    const roomIndexByFinalItem = new Map<number, number>();
    const seenRoomIds = new Set<number>();

    return rawItems.map((rawItem: any) => {
      const finalItemId = rawItem?.sale_final_item?.id;
      const roomIndex = roomIndexByFinalItem.get(finalItemId) || 0;
      roomIndexByFinalItem.set(finalItemId, roomIndex + 1);

      const peoplePerRoom = Math.max(0, Number(rawItem?.sale_final_item?.sale_offer_item?.people || 0));
      const request = rawItem?.sale_final_item?.sale_offer_item?.sale_request;
      const nightlyRequestByDate = this.buildNightlyRequestMap(request);
      const legacyRequestGuestNames = Array.isArray(request?.guest_names) ? request.guest_names : [];

      const nights: Array<FinalGuestNamesNightViewModel> = (Array.isArray(rawItem?.nights) ? rawItem.nights : []).map(
        (night: any) => {
          const dateKey = this.toDateKey(night?.date);
          const existingNames = Array.isArray(night?.guest_names) ? night.guest_names : [];
          const nightlyRequestSlot = dateKey ? nightlyRequestByDate.get(dateKey) : null;
          const requestSlotNames = Array.isArray(nightlyRequestSlot?.room_slots?.[roomIndex]?.guest_names)
            ? nightlyRequestSlot.room_slots[roomIndex].guest_names
            : legacyRequestGuestNames.slice(roomIndex * peoplePerRoom, (roomIndex + 1) * peoplePerRoom);

          const requestGuestNames = Array.from(
            { length: peoplePerRoom },
            (_, personIndex: number) => requestSlotNames[personIndex] || null
          );

          const guestNames = Array.from(
            { length: peoplePerRoom },
            (_, personIndex: number) => existingNames[personIndex] || requestGuestNames[personIndex] || null
          );

          return {
            date: night?.date,
            guestNames,
            requestGuestNames,
            manualOverride: !!night?.guest_names_manual_override,
          };
        }
      );

      const roomId = rawItem?.hotel_booking_room_id;
      const confirmationNumberDisabled = seenRoomIds.has(roomId);
      seenRoomIds.add(roomId);

      return {
        sale_final_item_id: finalItemId,
        hotel_booking_room_id: roomId,
        hotel_booking_room_confirmation_number: rawItem?.hotel_booking_room_confirmation_number,
        confirmationNumberDisabled,
        room: rawItem?.room,
        booking: rawItem?.booking,
        sale_final_item: rawItem?.sale_final_item,
        peoplePerRoom,
        nights,
      };
    });
  }

  private buildNightlyRequestMap(request: any): Map<string, any> {
    const map = new Map<string, any>();

    (Array.isArray(request?.nightly_guest_names) ? request.nightly_guest_names : []).forEach((night: any) => {
      const dateKey = this.toDateKey(night?.date);
      if (dateKey) {
        map.set(dateKey, night);
      }
    });

    return map;
  }

  private buildForm(): void {
    const itemGroups = this.items.map((item: FinalGuestNamesItemViewModel) =>
      this.formBuilder.group({
        sale_final_item_id: [item.sale_final_item_id],
        hotel_booking_room_id: [item.hotel_booking_room_id],
        hotel_booking_room_confirmation_number: [item.hotel_booking_room_confirmation_number],
        nights: new FormArray(
          item.nights.map((night: FinalGuestNamesNightViewModel) =>
            this.formBuilder.group({
              date: [night.date],
              guest_names: new FormArray(night.guestNames.map((name: string | null) => new FormControl(name))),
            })
          )
        ),
      })
    );

    this.form = this.formBuilder.group({ items: new FormArray(itemGroups) });
  }

  private formatPost(): { items: Array<any> } {
    return {
      items: this.itemGroups.controls.map((itemGroup: AbstractControl) => {
        const group = itemGroup as FormGroup;

        return {
          sale_final_item_id: group.get('sale_final_item_id')?.value,
          hotel_booking_room_id: group.get('hotel_booking_room_id')?.value,
          hotel_booking_room_confirmation_number: group.get('hotel_booking_room_confirmation_number')?.value,
          nights: this.getNights(group).controls.map((nightGroup: AbstractControl) => {
            const nightForm = nightGroup as FormGroup;

            return {
              date: this.toDateKey(nightForm.get('date')?.value),
              guest_names: this.getGuestNames(nightForm).getRawValue(),
            };
          }),
        };
      }),
    };
  }

  private toDateKey(value: any): string | null {
    if (!value) {
      return null;
    }

    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }
}
